import { Component, OnInit, ViewChild } from '@angular/core';

import { Router, RouterLink } from '@angular/router';
import { DevExtremeModule, DxDataGridComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import moment from 'moment';
import { AppInformationService, AuthService, CdrUnitOfWork, SessionInformationService } from 'src/app/core/core.module';
import { Customer } from 'src/app/core/model/customer';
import { CustomerRate } from 'src/app/core/model/customer-rate';
import { DialogService } from 'src/app/shared/dialog.service';
import { RateUpdateDialogComponent } from './rate-update-dialog/rate-update-dialog.component';

@Component({
    selector: 'app-customer-rates',
    templateUrl: './customer-rates.component.html',
    styleUrls: ['./customer-rates.component.scss'],
    standalone: true,
    imports: [DevExtremeModule, RouterLink]
})
export class CustomerRatesComponent implements OnInit {
    allCustomerRates: CustomerRate[];
    customerRates: CustomerRate[];
    rateIncreaseAmount: number;
    rateIncreaseDate: Date;
    jobSinceDate: Date;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    constructor(
        private uow: CdrUnitOfWork,
        private router: Router,
        private sessionInfo: SessionInformationService,
        private appInfo: AppInformationService,
        private auth: AuthService,
        private dialogService: DialogService
    ) {}

    async ngOnInit() {
        this.appInfo.pleaseWaitShow();
        let customerRates = await this.uow.getCustomerRates({
            expand: 'customer, dumpster, customerRateUpdates',
            orderBy: 'customer.companyName, rateTypeId, dumpster.size'
        });
        this.allCustomerRates = customerRates.filter(x => x.deleted == null && x.customer.deleted == null);
        this.allCustomerRates.forEach(rate => (rate.rateUpdatesHtml = this.buildRateUpdatesHtml(rate)));
        this.customerRates = this.allCustomerRates;
        this.appInfo.pleaseWaitHide();
    }

    async search() {
        if (!this.jobSinceDate) {
            this.customerRates = this.allCustomerRates;
            return;
        }

        this.appInfo.pleaseWaitShow();
        const customerIds = await this.uow.getCustomerIdsWithJobsSince(this.jobSinceDate);
        this.customerRates = this.allCustomerRates.filter(x => customerIds.has(x.customerId));
        this.appInfo.pleaseWaitHide();
    }

    rowDoubleClick(e) {
        this.open(e.data.customer);
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons'
            },
            {
                location: 'center',
                template: 'rateIncreaseButtons'
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    }
                }
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    }
                }
            }
        );
    }

    open(customer?: Customer) {
        if (!customer) return;
        this.router.navigate([`/customers/${customer.id}`]);
    }

    buildRateUpdatesHtml(rate: CustomerRate) {
        if (!rate.sortedRateUpdates.length) return '';

        const rows = rate.sortedRateUpdates
            .map(update => {
                const status = update.isPending ? 'Pending' : 'Completed';
                const statusClass = update.isPending ? 'pending' : 'completed';
                const rowClass = update.isPending ? 'rate-update-row' : '';
                const rowAction = update.isPending ? ' data-action="edit"' : '';
                const editIcon = update.isPending
                    ? `<i class="dx-icon dx-icon-edit rate-update-edit" data-action="edit" data-update-id="${update.id}" title="Edit"></i>`
                    : '';
                const deleteIcon = update.isPending
                    ? `<i class="dx-icon dx-icon-trash rate-update-delete" data-action="delete" data-update-id="${update.id}" title="Delete"></i>`
                    : '';
                return `<tr class="${rowClass}" data-update-id="${update.id}"${rowAction}>
                    <td>${moment(update.increaseDate).format('MM/DD/yy')}</td>
                    <td>${moment(update.created).format('MM/DD/yy')}</td>
                    <td>${update.amount != null ? '$' + update.amount.toFixed(2) : ''}</td>
                    <td><span class="rate-update-status ${statusClass}">${status}</span></td>
                    <td>${editIcon}${deleteIcon}</td>
                </tr>`;
            })
            .join('');

        return `<div class="rate-updates-cell"><table class="rate-updates-table">
            <thead>
                <tr>
                    <th>Scheduled</th>
                    <th>Created</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>${rows}</tbody>
        </table></div>`;
    }

    onRateUpdatesCellClick(e) {
        if (e.column?.dataField !== 'rateUpdatesHtml') return;

        const target = (e.event?.target as HTMLElement)?.closest('[data-action]') as HTMLElement;
        if (!target) return;

        const updateId = Number(target.getAttribute('data-update-id'));
        const rate = e.data as CustomerRate;

        if (target.getAttribute('data-action') === 'delete') this.deleteRateUpdate(rate, updateId);
        else this.editRateUpdate(rate, updateId);
    }

    async deleteRateUpdate(rate: CustomerRate, updateId: number) {
        const update = rate.customerRateUpdates?.find(u => u.id === updateId);
        if (!update) return;

        if (!(await confirm('Are you sure you want to delete this pending rate increase?', this.appInfo.appTitle))) return;

        this.appInfo.pleaseWaitShow();
        update.deleted = new Date();
        update.deletedBy = this.auth.userValue.userName;
        await this.uow.commit();
        rate.rateUpdatesHtml = this.buildRateUpdatesHtml(rate);
        this.appInfo.pleaseWaitHide();
        this.dataGrid.instance.refresh();
    }

    async editRateUpdate(rate: CustomerRate, updateId: number) {
        const update = rate.customerRateUpdates?.find(u => u.id === updateId);
        if (!update) return;

        const dialogRef = this.dialogService.open(RateUpdateDialogComponent);
        dialogRef.instance.update = update;
        const result = await dialogRef.instance.show();
        this.dialogService.close(dialogRef);

        if (result?.success) {
            rate.rateUpdatesHtml = this.buildRateUpdatesHtml(rate);
            this.dataGrid.instance.refresh();
        }
    }

    async applyRateIncrease() {
        const selectedRates = this.dataGrid.instance.getSelectedRowsData() as CustomerRate[];
        if (!selectedRates.length || this.rateIncreaseAmount == null ||!this.rateIncreaseDate) return;

        const increaseDate = moment(this.rateIncreaseDate).startOf('day').toDate();

        if (
            !(await confirm(
                `Apply a $${this.rateIncreaseAmount} rate increase to ${selectedRates.length} selected rate(s) effective ${moment(
                    increaseDate
                ).format('MM/DD/yyyy')}?`,
                this.appInfo.appTitle
            ))
        )
            return;

        this.appInfo.pleaseWaitShow();
        selectedRates.forEach(rate => {
            this.uow.customerRateUpdates.createEntity({
                customerRateId: rate.id,
                amount: this.rateIncreaseAmount,
                increaseDate,
                created: new Date(),
                createdBy: this.auth.userValue.userName
            });
        });
        await this.uow.commit();
        selectedRates.forEach(rate => (rate.rateUpdatesHtml = this.buildRateUpdatesHtml(rate)));
        this.appInfo.pleaseWaitHide();

        this.appInfo.notifyToast(`Rate increase applied to ${selectedRates.length} rate(s).`);
        this.rateIncreaseAmount = null;
        this.rateIncreaseDate = null;
        this.dataGrid.instance.deselectAll();
        this.dataGrid.instance.refresh();
    }
}
